import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { Loader2, Sparkles, Check, X, CheckCheck, Download } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { SecurityList } from "../analysis/SecuritySection";

const IMPACT = { high: "text-rose-300 border-rose-700/50", medium: "text-amber-300 border-amber-700/50", low: "text-emerald-300 border-emerald-700/50" };
const IMPACT_LABEL = { high: "Vysoký dopad", medium: "Stredný dopad", low: "Nízky dopad" };
const STATUS_LABEL = { approved: "Schválené", rejected: "Zamietnuté", done: "Hotovo" };

const EngineGrid = ({ check }) => (
  <div data-testid="client-ai-engines">
    <div className="flex items-end gap-3 mb-3">
      <p className="text-5xl font-black font-mono text-purple-300" data-testid="client-ai-score">{check.overall_visibility}</p>
      <p className="text-sm text-gray-400 pb-1.5">/ 100 AI viditeľnosť</p>
    </div>
    {check.summary && <p className="text-sm text-gray-300 mb-4">{check.summary}</p>}
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {check.engines.map((e) => (
        <div key={e.id} className="p-3 bg-gray-900 border border-gray-800 rounded-sm" data-testid={`client-ai-engine-${e.id}`}>
          <p className="text-xs text-gray-400">{e.name}</p>
          <p className={`text-xl font-black font-mono ${e.mentioned ? "text-emerald-400" : "text-gray-300"}`}>{e.citation_score}</p>
          <p className="text-[11px] text-gray-500 truncate">{e.mentioned ? "Pravdepodobne spomína" : "Pravdepodobne nespomína"}</p>
        </div>
      ))}
    </div>
    <p className="mt-2 text-[11px] text-gray-500">Odhad na základe AI analýzy, nie priame merania jednotlivých platforiem. Kontrola beží automaticky raz týždenne.</p>
  </div>
);

const StepCard = ({ step, onDecide, busy }) => (
  <div className="p-4 bg-gray-900 border border-gray-800 rounded-sm" data-testid={`agent-step-${step.id}`}>
    <div className="flex flex-wrap items-center gap-2 mb-1.5">
      <p className="font-bold text-white">{step.title}</p>
      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border rounded-sm ${IMPACT[step.impact] || IMPACT.medium}`}>{IMPACT_LABEL[step.impact] || step.impact}</span>
    </div>
    <p className="text-sm text-gray-400 mb-3">{step.description}</p>
    {step.status === "pending" ? (
      <div className="flex gap-2">
        <button disabled={busy} onClick={() => onDecide(step.id, "approved")} data-testid={`agent-step-approve-${step.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-sm disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Schváliť</button>
        <button disabled={busy} onClick={() => onDecide(step.id, "rejected")} data-testid={`agent-step-reject-${step.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-700 text-gray-300 hover:text-white text-xs font-bold rounded-sm disabled:opacity-50"><X className="w-3.5 h-3.5" /> Zamietnuť</button>
      </div>
    ) : (
      <div className="flex items-center gap-3">
        <span className="text-xs font-bold text-gray-400" data-testid={`agent-step-status-${step.id}`}>{STATUS_LABEL[step.status]}</span>
        {step.status === "approved" && (
          <button disabled={busy} onClick={() => onDecide(step.id, "done")} data-testid={`agent-step-done-${step.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-400 hover:text-emerald-300 disabled:opacity-50"><CheckCheck className="w-3.5 h-3.5" /> Označiť ako hotové</button>
        )}
      </div>
    )}
  </div>
);

export const agencyAuth = (userEmail) => {
  const token = localStorage.getItem("trp_token");
  return {
    withCredentials: true,
    headers: { "X-User-Email": userEmail, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  };
};

export function AgencyClientAiPanel({ userEmail, clientId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const auth = agencyAuth(userEmail);

  const load = useCallback(async () => {
    if (!clientId) return;
    setData(null);
    const r = await axios.get(`${API}/agency/clients/${clientId}/ai`, agencyAuth(userEmail));
    setData(r.data);
  }, [clientId, userEmail]);
  useEffect(() => { load(); }, [load]);

  const runCheck = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/agency/clients/${clientId}/ai-check`, {}, auth);
      toast.success("AI kontrola dokončená");
      await load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI kontrola zlyhala"); }
    setChecking(false);
  };

  const decide = async (stepId, decision) => {
    setBusy(true);
    try {
      await axios.post(`${API}/agency/steps/${stepId}/decision`, { decision }, auth);
      await load(); onChanged();
    } catch { toast.error("Uloženie zlyhalo"); }
    setBusy(false);
  };

  const downloadPdf = async () => {
    const r = await axios.get(`${API}/agency/clients/${clientId}/report.pdf`, { ...auth, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${data?.client?.slug || "klient"}-report.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const pending = data?.steps.filter((s) => s.status === "pending") || [];
  const decided = data?.steps.filter((s) => s.status !== "pending") || [];

  return (
    <Sheet open={!!clientId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-black border-gray-800 text-white overflow-y-auto" data-testid="client-ai-panel">
        <SheetHeader><SheetTitle className="text-white text-left">{data?.client?.name || "Klient"}</SheetTitle></SheetHeader>
        {!data ? (
          <div className="py-10 flex items-center gap-2 text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Načítam…</div>
        ) : (
          <div className="space-y-6 mt-4">
            <div className="flex flex-wrap gap-2">
              <button onClick={runCheck} disabled={checking} data-testid="client-ai-run-check" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold rounded-sm disabled:opacity-50">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {checking ? "Kontrolujem… (do 30 s)" : "Spustiť AI kontrolu"}
              </button>
              <button onClick={downloadPdf} data-testid="client-ai-download-pdf" className="inline-flex items-center gap-2 px-4 py-2 border border-gray-700 hover:border-gray-500 text-sm font-bold rounded-sm">
                <Download className="w-4 h-4" /> Report s vaším logom
              </button>
            </div>
            {data.latest ? <EngineGrid check={data.latest} /> : <p className="text-sm text-gray-400" data-testid="client-ai-no-check">Tento klient ešte nemá AI kontrolu.</p>}
            {data.latest?.security && (
              <div className="p-4 bg-gray-900 border border-gray-800 rounded-sm" data-testid="client-security">
                <h3 className="text-xs uppercase tracking-widest text-purple-400 mb-3">Bezpečnosť webu</h3>
                <SecurityList security={data.latest.security} />
              </div>
            )}
            {pending.length > 0 && (
              <div className="space-y-3" data-testid="client-ai-pending-steps">
                <h3 className="text-xs uppercase tracking-widest text-purple-400">Kroky na schválenie ({pending.length})</h3>
                {pending.map((s) => <StepCard key={s.id} step={s} onDecide={decide} busy={busy} />)}
              </div>
            )}
            {decided.length > 0 && (
              <div className="space-y-3" data-testid="client-ai-decided-steps">
                <h3 className="text-xs uppercase tracking-widest text-gray-500">Rozhodnuté</h3>
                {decided.map((s) => <StepCard key={s.id} step={s} onDecide={decide} busy={busy} />)}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
